import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import App from "./App";
import { installAuthFetch } from "./auth";
import { installGlobalUxTelemetry } from "./telemetry/uxTelemetry";
import "./index.css";

const MOCK_API_PREFIXES = ["/api/", "/health"] as const;

function isMockApiRequest(input: RequestInfo | URL): boolean {
  const rawUrl =
    typeof input === "string"
      ? input
      : input instanceof URL
        ? input.toString()
        : input.url;

  try {
    const pathname = new URL(rawUrl, window.location.origin).pathname;
    return MOCK_API_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(prefix));
  } catch {
    return false;
  }
}

function installDeferredMockFetch(): Promise<void> {
  let mockReady: Promise<void> | null = null;
  const originalFetch = globalThis.fetch.bind(globalThis);

  const ensureMockReady = (): Promise<void> => {
    if (!mockReady) {
      mockReady = import("./mock-data").then(({ setupMockFetch }) => {
        setupMockFetch();
      });
    }
    return mockReady;
  };

  globalThis.fetch = (async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    if (!isMockApiRequest(input)) {
      return originalFetch(input, init);
    }

    await ensureMockReady();
    return globalThis.fetch(input, init);
  }) as typeof fetch;

  return ensureMockReady();
}

async function boot(): Promise<void> {
  // Activate mock API when running in preview mode (VITE_MOCK=true)
  if (import.meta.env.VITE_MOCK === "true") {
    void installDeferredMockFetch();
  }

  installAuthFetch();
  installGlobalUxTelemetry();

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: 2,
        refetchOnWindowFocus: false,
      },
    },
  });

  const root = document.getElementById("root");
  if (!root) throw new Error("Root element not found");

  ReactDOM.createRoot(root).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </React.StrictMode>,
  );
}

boot();
